import * as React from "react";
import { cn } from "../utils/cn";
import { useGlobalI18n } from "../contexts/GlobalI18nContext";

/** Public props for the `Badge` component. */
interface BadgeProps {
  children?: React.ReactNode;
  variant?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger"
    | "destructive"
    | "info"
    | "outline"
    | "ghost"
    | "transparent"
    | "gradient";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
  dot?: boolean;
  count?: number;
  maxCount?: number;
  showZero?: boolean;
  pulse?: boolean;
  removable?: boolean;
  onRemove?: () => void;
  icon?: React.ComponentType<{ className?: string }>;
  clickable?: boolean;
  onClick?: () => void;
}

const variantStyles = {
  default: "bg-muted text-muted-foreground border-border/50",
  primary: "bg-primary text-primary-foreground border-primary/20",
  secondary: "bg-secondary text-secondary-foreground border-secondary/20",
  success: "bg-success text-success-foreground border-success/20",
  warning: "bg-warning text-warning-foreground border-warning/20",
  danger: "bg-destructive text-destructive-foreground border-destructive/20",
  destructive: "bg-destructive text-destructive-foreground border-destructive/20",
  info: "bg-info text-info-foreground border-info/20",
  outline: "bg-transparent text-foreground border-border/50",
  ghost: "bg-transparent text-foreground border-transparent",
  transparent: "bg-transparent text-foreground border-transparent",
  gradient: "bg-linear-to-r from-primary to-secondary text-primary-foreground border-transparent",
};

// Hover styles chỉ dùng khi clickable=true
const clickableVariantStyles = {
  default: "hover:bg-muted/80",
  primary: "hover:bg-primary/90",
  secondary: "hover:bg-secondary/90",
  success: "hover:bg-success/90",
  warning: "hover:bg-warning/90",
  danger: "hover:bg-destructive/90",
  destructive: "hover:bg-destructive/90",
  info: "hover:bg-info/90",
  outline: "hover:bg-accent/50",
  ghost: "hover:bg-accent/20 hover:text-accent-foreground",
  transparent: "hover:bg-accent/30",
  gradient: "hover:from-primary/90 hover:to-secondary/90",
};

const sizeStyles = {
  xs: "px-1.5 py-0.5 text-xs font-medium min-h-4.5",
  sm: "px-2 py-0.5 text-xs font-medium min-h-5",
  md: "px-2.5 py-1 text-xs font-medium min-h-6",
  lg: "px-3 py-1 text-xs font-medium min-h-6.5",
  xl: "px-3.5 py-1.5 text-sm font-medium min-h-7",
};

const dotSizeStyles = {
  xs: "w-1.5 h-1.5",
  sm: "w-2 h-2",
  md: "w-2.5 h-2.5",
  lg: "w-3 h-3",
  xl: "w-4 h-4",
};

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = "default",
  size = "md",
  className,
  dot = false,
  count,
  maxCount = 99,
  showZero = false,
  pulse = false,
  removable = false,
  onRemove,
  icon,
  clickable = false,
  onClick,
}) => {
  const gi18n = useGlobalI18n();
  const isCountBadge = typeof count === "number";
  const shouldShowCount = isCountBadge && (count > 0 || showZero);
  const displayCount = count && count > maxCount ? `${maxCount}+` : count;
  const Icon = icon;

  const handleClick = (e: React.MouseEvent) => {
    if (clickable && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove?.();
  };

  const baseClasses = cn(
    "inline-flex items-center rounded-md border transition-all duration-200 tracking-wide",
    "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
    variantStyles[variant],
    clickable && clickableVariantStyles[variant],
    clickable && "cursor-pointer hover:shadow-sm active:scale-95",
    pulse && "animate-pulse",
    "relative z-0", // Ensure badges stay below sticky headers (z-20, z-50)
  );

  if (dot) {
    return (
      <span
        className={cn(
          "inline-flex rounded-full border",
          dotSizeStyles[size],
          variantStyles[variant],
          pulse && "animate-pulse",
          clickable && "cursor-pointer hover:scale-110",
          className,
        )}
        onClick={handleClick}
      />
    );
  }

  if (shouldShowCount) {
    return (
      <span
        className={cn(
          baseClasses,
          "justify-center rounded-full",
          "min-w-6 h-6 px-1.5 text-xs font-bold",
          size === "xs" && "min-w-5 h-5 px-1 text-xs",
          size === "sm" && "min-w-6 h-6 px-1.5 text-xs",
          size === "md" && "min-w-7 h-7 px-2 text-xs",
          size === "lg" && "min-w-8 h-8 px-2.5 text-sm",
          size === "xl" && "min-w-9 h-9 px-3 text-sm",
          className,
        )}
        onClick={handleClick}
        role="status"
        aria-live="polite"
      >
        {displayCount}
      </span>
    );
  }

  return (
    <span className={cn(baseClasses, "gap-1", sizeStyles[size], className)} onClick={handleClick} role="status">
      {Icon && (
        <Icon
          className={cn(
            "shrink-0",
            size === "xs" && "h-3 w-3",
            size === "sm" && "h-3 w-3",
            size === "md" && "h-4 w-4",
            size === "lg" && "h-4 w-4",
            size === "xl" && "h-5 w-5",
          )}
        />
      )}

      {children}

      {removable && (
        <button
          onClick={handleRemove}
          className={cn(
            "ml-1 rounded-full hover:bg-accent focus:outline-none focus:bg-accent",
            "transition-colors duration-150 shrink-0",
            size === "xs" && "h-3 w-3",
            size === "sm" && "h-3 w-3",
            size === "md" && "h-4 w-4",
            size === "lg" && "h-4 w-4",
            size === "xl" && "h-5 w-5",
          )}
          aria-label={gi18n.removeBadge ?? "Remove badge"}
        >
          <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </span>
  );
};

// Notification Badge component - wrapper for positioning badges over other elements
/** Public props for the `NotificationBadge` component. */
interface NotificationBadgeProps {
  children: React.ReactNode;
  count?: number;
  maxCount?: number;
  showZero?: boolean;
  dot?: boolean;
  variant?: BadgeProps["variant"];
  size?: BadgeProps["size"];
  className?: string;
  badgeClassName?: string;
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  pulse?: boolean;
}

const positionStyles = {
  "top-right": "-top-1 -right-1",
  "top-left": "-top-1 -left-1",
  "bottom-right": "-bottom-1 -right-1",
  "bottom-left": "-bottom-1 -left-1",
};

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  children,
  count,
  maxCount = 99,
  showZero = false,
  dot = false,
  variant = "danger",
  size = "sm",
  className,
  badgeClassName,
  position = "top-right",
  pulse = false,
}) => {
  const shouldShow = dot || (typeof count === "number" && (count > 0 || showZero));

  return (
    <div className={cn("relative inline-flex", className)}>
      {children}
      {shouldShow && (
        <Badge
          count={count}
          maxCount={maxCount}
          showZero={showZero}
          dot={dot}
          variant={variant}
          size={size}
          pulse={pulse}
          className={cn("absolute transform scale-100 transition-transform duration-200", positionStyles[position], "shadow-sm", badgeClassName)}
          aria-live="polite"
        />
      )}
    </div>
  );
};

// Specialized Badge components
/** Public props for the `StatusBadge` component. */
interface StatusBadgeProps extends Omit<BadgeProps, "variant"> {
  status?: "online" | "offline" | "busy" | "away" | "idle";
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status = "offline", ...props }) => {
  const statusVariants = {
    online: "success",
    offline: "default",
    busy: "danger",
    away: "warning",
    idle: "info",
  } as const;

  return <Badge {...props} variant={statusVariants[status]} dot={true} pulse={status === "online"} />;
};

/** Public props for the `TagBadge` component. */
interface TagBadgeProps extends Omit<BadgeProps, "removable"> {
  tags?: string[];
  onTagRemove?: (tag: string) => void;
  maxTags?: number;
}

export const TagBadge: React.FC<TagBadgeProps> = ({ tags = [], onTagRemove, maxTags = 3, size = "sm", variant = "outline", className, ...props }) => {
  const displayTags = tags.slice(0, maxTags);
  const remainingCount = tags.length - maxTags;

  return (
    <div className={cn("flex flex-wrap gap-1", className)}>
      {displayTags.map((tag, index) => (
        <Badge key={`${tag}-${index}`} {...props} variant={variant} size={size} removable={!!onTagRemove} onRemove={() => onTagRemove?.(tag)}>
          {tag}
        </Badge>
      ))}
      {remainingCount > 0 && (
        <Badge variant="ghost" size={size}>
          +{remainingCount}
        </Badge>
      )}
    </div>
  );
};

/** Public props for the `InteractiveBadge` component. */
interface InteractiveBadgeProps extends BadgeProps {
  active?: boolean;
  disabled?: boolean;
}

export const InteractiveBadge: React.FC<InteractiveBadgeProps> = ({ active = false, disabled = false, variant = "outline", className, ...props }) => {
  return (
    <Badge
      {...props}
      variant={active ? "primary" : variant}
      clickable={!disabled}
      className={cn("select-none", disabled && "opacity-50 cursor-not-allowed", !disabled && !active && "hover:border-primary/50", className)}
    />
  );
};

/** Public props for the `GradientBadge` component. */
interface GradientBadgeProps extends Omit<BadgeProps, "variant"> {
  from?: string;
  to?: string;
}

export const GradientBadge: React.FC<GradientBadgeProps> = ({ from = "from-primary", to = "to-secondary", className, ...props }) => {
  return (
    <Badge
      {...props}
      variant="transparent"
      className={cn(`bg-linear-to-r ${from} ${to} text-primary-foreground border-transparent`, "hover:opacity-90", className)}
    />
  );
};

/** Public props for the `PulseBadge` component. */
interface PulseBadgeProps extends BadgeProps {
  speed?: "slow" | "normal" | "fast";
}

export const PulseBadge: React.FC<PulseBadgeProps> = ({ speed = "normal", className, ...props }) => {
  const speedClasses = {
    slow: "animate-pulse [animation-duration:2s]",
    normal: "animate-pulse",
    fast: "animate-pulse [animation-duration:0.5s]",
  };

  return <Badge {...props} pulse={false} className={cn(speedClasses[speed], className)} />;
};

export default Badge;
